'use client';
import React, { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet';
import { gsap } from 'gsap';
import Footer from '../blog-details/Footer';
import Lines from '../common/Lines';
import ProgressScroll from '../common/ProgressScroll';
import Cursor from '../common/cusor';
import LoadingScreen from '../common/loader';
import Navbar from '../creative-agency/Navbar';

function InitiativeDetail({ initiative }) {
  useLayoutEffect(() => {
    const tl = gsap.timeline();
    tl.fromTo('.header', { y: 200 }, { y: 0 }, '+=2.5');
    tl.fromTo(
      '.header .container',
      { opacity: 0, translateY: 40 },
      { opacity: 1, translateY: 0 },
      '-=0'
    );
    return () => tl.kill();
  }, []);

  const { title, category, year, location, heroImage, need, approach, stats, next } =
    initiative;

  return (
    <>
      <Helmet>
        <title>{title} — SPOB Nigeria</title>
        <meta name="description" content={need?.headline || `Learn about SPOB Nigeria's ${title} initiative.`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${title} — SPOB Nigeria`} />
        <meta property="og:description" content={need?.headline || `Learn about SPOB Nigeria's ${title} initiative.`} />
        <meta property="og:image" content={heroImage ? `https://spob-nigeria.vercel.app${heroImage}` : 'https://spob-nigeria.vercel.app/light/assets/imgs/spob/spob-1.jpg'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} — SPOB Nigeria`} />
        <meta name="twitter:description" content={need?.headline || `Learn about SPOB Nigeria's ${title} initiative.`} />
        <meta name="twitter:image" content={heroImage ? `https://spob-nigeria.vercel.app${heroImage}` : 'https://spob-nigeria.vercel.app/light/assets/imgs/spob/spob-1.jpg'} />
        <link rel="icon" href="/light/imgs/favicon.ico" />
        <link rel="shortcut icon" href="/light/imgs/favicon.ico" />
        <link
          rel="stylesheet"
          type="text/css"
          href="/light/assets/css/plugins.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="/light/assets/css/style.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="/light/assets/css/satoshi.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://fonts.googleapis.com/css?family=Poppins:100,200,300,400,500,600,700,800,900&display=swap"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght..200;300;400;500;600;700&display=swap"
        />
        <script src="/light/assets/js/scripts.js"></script>
        <script src="/light/assets/js/smoother-script.js"></script>
      </Helmet>
      <body>
        <LoadingScreen />
        <Cursor />
        <ProgressScroll />
        <Lines />
        <div id="smooth-wrapper">
          <Navbar />
          <div id="smooth-content">
            <main className="main-bg">
              {/* ── HERO HEADER ── */}
              <div className="header header-project1">
                <div className="container pt-100">
                  <div className="row align-items-end">
                    <div className="col-lg-8 md-mb50">
                      <h6 className="sub-title main-color mb-20 fz-12 text-u ls3">
                        {category}
                      </h6>
                      <h1 className="fz-55 fw-600">{title}</h1>
                    </div>
                    <div className="col-lg-4">
                      <div className="info">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="item mb-30">
                              <span className="opacity-8 mb-5">Programme :</span>
                              <h6>{category}</h6>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="item mb-30">
                              <span className="opacity-8 mb-5">By :</span>
                              <h6>SPOB Nigeria</h6>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="item mb-30">
                              <span className="opacity-8 mb-5">Location :</span>
                              <h6>{location}</h6>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="item">
                              <span className="opacity-8 mb-5">Since :</span>
                              <h6>{year}</h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="bg-img mt-100"
                  data-background={heroImage}
                ></div>
              </div>

              {/* ── THE NEED ── */}
              <section className="section-padding bord-thin-bottom">
                <div className="container">
                  <div className="row">
                    <div className="col-lg-5">
                      <h2 className="mb-50">
                        The
                        <br />
                        Need
                      </h2>
                    </div>
                    <div className="col-lg-7">
                      <div className="text">
                        <h5 className="mb-30 fw-400 line-height-40">
                          {need.headline}
                        </h5>
                        <p className="fz-18">{need.body}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* ── IMPACT STATS ── */}
              <section className="section-padding sub-bg bord-thin-bottom">
                <div className="container">
                  <div className="sec-head mb-60">
                    <h6 className="sub-title main-color mb-15">Our Impact</h6>
                  </div>
                  <div className="row">
                    {stats.map((stat, i) => (
                      <div
                        className="col-lg-4 col-md-4 text-center md-mb50"
                        key={i}
                      >
                        <h2 className="main-color fz-60 fw-600">{stat.number}</h2>
                        <p className="fz-16 mt-10 opacity-8">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* ── OUR APPROACH ── */}
              <section className="section-padding bord-thin-bottom">
                <div className="container">
                  <div className="row justify-content-end">
                    <div className="col-lg-7">
                      <div className="text">
                        <h2 className="mb-50">
                          Our
                          <br />
                          Approach
                        </h2>
                        <h5 className="mb-30 fw-400 line-height-40">
                          {approach.headline}
                        </h5>
                        <p className="fz-18">{approach.body}</p>
                        <a
                          href="/light/page-contact"
                          className="butn butn-md butn-bord radius-30 mt-40 d-inline-flex"
                        >
                          <span>Get Involved</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* ── NEXT INITIATIVE ── */}
              <section className="next-project pt-60 pb-60 bord-thin-top">
                <div className="container">
                  <div className="row justify-content-between align-items-center">
                    <div className="col-lg-9 col-md-9">
                      <div className="text">
                        <h6 className="sub-title main-color mb-15 fz-12 text-u ls3">
                          Next Initiative
                        </h6>
                        <h3>{next.title}</h3>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-3 text-right">
                      <a href={next.href} className="go-more">
                        <span className="icon ti-arrow-top-right"></span>
                      </a>
                    </div>
                  </div>
                </div>
              </section>
            </main>
            <Footer />
          </div>
        </div>
      </body>
    </>
  );
}

export default InitiativeDetail;
